import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Paper, Typography, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { authHeader } from "../../auth";

const POLL_MS = 2000;

/**
 * FisStatusMini — version compacte du panneau FIS (statut + START/STOP +
 * "Forcer un envoi complet"), pensée pour être intégrée directement dans
 * la vue de chronométrage (TimingChronoPage), afin que le statut FIS
 * reste visible et actionnable sans changer d'onglet.
 *
 * ⚠️ La connexion FIS elle-même vit côté SERVEUR (Map de clients +
 * boucle de fond dans timingFisRouter.js) — elle continue de fonctionner
 * même si aucun composant ne l'affiche. Ce widget ne fait qu'AFFICHER et
 * PILOTER cet état, il ne le maintient pas en vie lui-même.
 *
 * Pour configurer le raceinfo/codex/password/mode de transport, utiliser
 * la page complète /timingsoftware/fis/:eventKey (lien "Configurer" ici).
 *
 * Modifié le 01.10.2026 :
 *   - une réponse d'erreur du polling n'écrase plus l'état (avant, un 500
 *     de /status faisait afficher "Déconnecté" alors que tout tournait) ;
 *   - "Online" suit le champ connected renvoyé par le serveur, qui en
 *     HTTPS signifie "démarré" (pas de connexion permanente en HTTPS) ;
 *   - mode parallèle : pas de boucle d'envoi automatique, donc pas
 *     d'alerte "envoi automatique en pause" à tort ;
 *   - les avertissements/compte-rendus de START et de "Forcer un envoi"
 *     sont affichés (avant ils étaient ignorés).
 */
export default function FisStatusMini({ eventKey, run }) {
    const navigate = useNavigate();
    const [status, setStatus] = useState({ connected: false, synced: false });
    const [error, setError] = useState("");
    const [info, setInfo] = useState("");
    const [busy, setBusy] = useState(false);
    const [pollFailed, setPollFailed] = useState(false);
    const busyRef = useRef(false);
    busyRef.current = busy;

    const apiFetch = useCallback(
        (url, options = {}) =>
            fetch(url, { ...options, headers: { "Content-Type": "application/json", ...authHeader(), ...(options.headers || {}) } }),
        []
    );

    const poll = useCallback(async () => {
        // Pas de polling pendant un START/STOP : évite qu'une réponse
        // arrivée entre-temps ne remette un état périmé.
        if (busyRef.current) return;
        try {
            const res = await apiFetch(`/api/timing/fis/${encodeURIComponent(eventKey)}/status`);
            if (!res.ok) {
                setPollFailed(true);
                return; // on garde le dernier état connu
            }
            const data = await res.json();
            setStatus(data);
            setPollFailed(false);
        } catch {
            setPollFailed(true);
        }
    }, [eventKey, apiFetch]);

    useEffect(() => {
        poll();
        const t = setInterval(poll, POLL_MS);
        return () => clearInterval(t);
    }, [poll]);

    const toggleStartStop = async () => {
        setBusy(true);
        setError("");
        setInfo("");
        try {
            if (status.connected) {
                const res = await apiFetch(`/api/timing/fis/${encodeURIComponent(eventKey)}/stop`, { method: "POST" });
                if (!res.ok) {
                    const data = await res.json().catch(() => ({}));
                    setError(data.error || `HTTP ${res.status}`);
                }
            } else {
                // /start relit la config déjà enregistrée côté serveur
                // (raceinfo, codex, password, mode...) — pas besoin de la
                // renvoyer depuis ce widget compact. Si rien n'est encore
                // configuré, le serveur répond une erreur explicite.
                const res = await apiFetch(`/api/timing/fis/${encodeURIComponent(eventKey)}/start`, { method: "POST" });
                const data = await res.json().catch(() => ({}));
                if (res.status === 202) {
                    setInfo(data.error || "Connexion en cours…");
                } else if (!res.ok) {
                    setError(data.error || `HTTP ${res.status}`);
                } else if (data.warning) {
                    setError(data.warning);
                } else if (data.info) {
                    setInfo(data.info);
                }
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
            // État réel relu au serveur, plutôt que deviné ici.
            setTimeout(poll, 0);
        }
    };

    const forceFullSend = async () => {
        setBusy(true);
        setError("");
        setInfo("");
        try {
            const res = await apiFetch(`/api/timing/fis/${encodeURIComponent(eventKey)}/send`, { method: "POST" });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) setError(data.error || `HTTP ${res.status}`);
            else setInfo(data.info || "Envoi complet effectué ✓");
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
            setTimeout(poll, 0);
        }
    };

    const isPar = status.mode === "par";

    return (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                        Serveur FIS{isPar ? " — parallèle" : ""}
                    </Typography>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: status.connected ? "success.main" : "error.main" }} />
                        <Typography variant="caption">{status.connected ? "Online" : "Arrêté"}</Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: status.synced ? "success.main" : "warning.main" }} />
                        <Typography variant="caption">{status.synced ? "Sync" : "No Sync"}</Typography>
                    </Box>

                    {status.connected && !isPar && status.autoPushActive && (
                        <Typography variant="caption" color="text.secondary">
                            ● Envoi automatique actif
                        </Typography>
                    )}
                    {status.connected && !isPar && !status.autoPushActive && (
                        <Typography variant="caption" color="warning.main">
                            ⚠ Envoi automatique en pause (échecs répétés) — STOP puis START
                        </Typography>
                    )}
                    {status.connected && isPar && (
                        <Typography variant="caption" color="text.secondary">
                            ● Envoi à chaque action du bracket
                        </Typography>
                    )}
                    {pollFailed && (
                        <Typography variant="caption" color="warning.main">
                            (statut momentanément indisponible)
                        </Typography>
                    )}
                </Box>

                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                        size="small"
                        onClick={() =>
                            navigate(
                                `/timingsoftware/fis/${encodeURIComponent(eventKey)}${run ? `?run=${encodeURIComponent(run)}` : ""}`
                            )
                        }
                    >
                        Configurer
                    </Button>
                    <Button size="small" variant="outlined" onClick={forceFullSend} disabled={!status.connected || busy}>
                        Forcer un envoi
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        color={status.connected ? "error" : "success"}
                        onClick={toggleStartStop}
                        disabled={busy}
                    >
                        {busy ? "…" : status.connected ? "STOP" : "START"}
                    </Button>
                </Box>
            </Box>

            {(error || (status.connected && status.lastError)) && (
                <Typography color="error" variant="caption" display="block" sx={{ mt: 0.5, whiteSpace: "pre-line" }}>
                    {error || status.lastError}
                </Typography>
            )}
            {info && !error && (
                <Typography color="text.secondary" variant="caption" display="block" sx={{ mt: 0.5, whiteSpace: "pre-line" }}>
                    {info}
                </Typography>
            )}
        </Paper>
    );
}